import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '../styled';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Dialog } from '../Dialog';
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogFooterActions,
  DialogHeader,
  DialogTitle,
  DialogTitleBlock,
} from '../DialogShell';

const ItemList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const Item = styled('label')({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 12,
  alignItems: 'flex-start',
  padding: 12,
  borderRadius: 8,
  border: '1px solid var(--qhs-border)',
  background: 'var(--qhs-surface)',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, background-color 140ms ease',
  '&:hover': {
    borderColor: 'var(--qhs-brand)',
    background: 'rgba(46, 72, 211, 0.03)',
  },
  '&:has(input:checked)': {
    borderColor: 'var(--qhs-brand)',
    background: 'rgba(46, 72, 211, 0.05)',
  },
});

const ItemText = styled('span')({
  fontSize: 13,
  lineHeight: 1.55,
  color: 'var(--qhs-text-1)',
});

/**
 * Multi-checkbox confirmation dialog.
 *
 * Used when a backend mutation returns 422 with `requiredConfirmations`
 * — each item describes a soft validation the user must explicitly
 * acknowledge before retry. Confirm button is disabled until every
 * item is ticked. On confirm, the selected keys are passed back to
 * the caller (`onConfirm(keys)`) which retries the mutation with
 * `body.confirmations = keys`.
 *
 * Props:
 *   - open: boolean
 *   - items: Array<{ key: string, localizedMessage: string }>
 *   - title?: string                — defaults to dialogs.confirmResend
 *   - description?: string          — optional sub-title under the headline
 *   - onCancel: () => void
 *   - onConfirm: (keys: string[]) => Promise<void> | void
 *   - isLoading?: boolean           — disables Confirm + close while in-flight
 *   - confirmLabel?: string         — defaults to btn.confirmAndContinue
 *   - cancelLabel?: string          — defaults to btn.cancel
 */
const ConfirmationsDialog = ({
  open,
  items,
  title,
  description,
  onCancel,
  onConfirm,
  isLoading,
  confirmLabel,
  cancelLabel,
}) => {
  const { btn, dialogs } = useSelector(state => state.i18n);
  const [selected, setSelected] = useState([]);

  // Reset acknowledgements every time the dialog opens with a fresh
  // set. Without this the previous run's checkboxes can carry over
  // when the same item key reappears in a new resend cycle.
  useEffect(() => {
    if (open) setSelected([]);
  }, [open, items]);

  const allTicked = items?.length > 0 && selected.length === items.length;

  const toggle = key =>
    setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  const handleConfirm = async () => {
    if (!allTicked) return;
    await onConfirm(selected);
  };

  const handleCancel = () => {
    if (isLoading) return;
    onCancel?.();
  };

  const resolvedTitle = title || dialogs?.confirmResend || 'Confirm to send anyway';

  return (
    <Dialog
      open={!!open}
      onClose={handleCancel}
      ariaLabel={resolvedTitle}
      maxWidth={520}
      header={
        <DialogHeader>
          <DialogTitleBlock>
            <DialogTitle>{resolvedTitle}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogTitleBlock>
        </DialogHeader>
      }
      footer={
        <DialogFooter>
          <DialogFooterActions>
            <Button variant='ghost' onClick={handleCancel} disabled={isLoading}>
              {cancelLabel || btn?.cancel}
            </Button>
            <Button
              variant='primary'
              onClick={handleConfirm}
              disabled={!allTicked || isLoading}
              loading={isLoading}
            >
              {confirmLabel || btn?.confirmAndContinue}
            </Button>
          </DialogFooterActions>
        </DialogFooter>
      }
    >
      <DialogBody>
        <ItemList>
          {(items ?? []).map(item => (
            <Item key={item.key}>
              <Checkbox
                checked={selected.includes(item.key)}
                onChange={() => toggle(item.key)}
              />
              <ItemText>{item.localizedMessage}</ItemText>
            </Item>
          ))}
        </ItemList>
      </DialogBody>
    </Dialog>
  );
};

export default ConfirmationsDialog;
